import { useEffect, useState } from "react";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import {
  ClioService,
  ClioTasksInfo,
  CustomFields,
  MatterPreviewResponse,
  UsersModel,
} from "../../api";

const MySwal = withReactContent(Swal);

export default function ClioTaskAssignees() {
  const [allMatters, setAllMatters] = useState<MatterPreviewResponse[]>([]);
  const [filteredMatters, setFilteredMatters] = useState<MatterPreviewResponse[]>([]);
  const [allTasks, setAllTasks] = useState<ClioTasksInfo[]>([]);
  const [users, setUsers] = useState<UsersModel[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [usersTypesList, setUsersTypesList] = useState<CustomFields[]>([CustomFields.ASSIGNED_PARALEGAL, CustomFields.RESPONSIBLE_ATTORNEY, CustomFields.SUPPORTING_ATTY, CustomFields.SENIOR_MANAGING_ATTY, CustomFields.LEGAL_ASSISTANT]);
  const [selectedUserTypes, setSelectedUserTypes] = useState<string | null>(null);
  const [selectedMatters, setSelectedMatters] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    ClioService.getApiClioMatters("").then((matters) => {
      const sortedMatters = matters.sort(
        (m1, m2) =>
          m1.description?.localeCompare(m2.description ?? "") ?? -1
      );
      setAllMatters(sortedMatters);
      setFilteredMatters(sortedMatters);
    });

    ClioService.getApiClioTaskData().then((tasks) => {
      const sortedTasks = tasks.sort(
        (t1, t2) => t1.name?.localeCompare(t2.name ?? "") ?? -1
      );
      setAllTasks(sortedTasks);
    });
    ClioService.getApiClioClioUsers(true).then(clioUsers => setUsers(clioUsers));
  }, []);

  // Search filter
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredMatters(allMatters);
      return;
    }
    const lower = searchTerm.toLowerCase();
    setFilteredMatters(
      allMatters.filter(
        (m) =>
          m.description?.toLowerCase().includes(lower)
      )
    );
    setPage(0);
  }, [searchTerm, allMatters]);

  // Toggle selection
  const toggleMatter = (id: number) => {
    setSelectedMatters((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id]
    );
  };

  const toggleAll = (checked: boolean) => {
    if (checked) {
      const visibleIds = filteredMatters
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((m) => m.id!);
      setSelectedMatters((prev) => Array.from(new Set([...prev, ...visibleIds])));
    } else {
      const visibleIds = filteredMatters
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((m) => m.id);
      setSelectedMatters((prev) => prev.filter((id) => !visibleIds.includes(id)));
    }
  };

  const handleAssignTask = async () => {
    if (!selectedUser || selectedMatters.length === 0 || !selectedUserTypes) {
      MySwal.fire({
        icon: "warning",
        title: "Select at least one matter and one task type.",
      });
      return;
    }

    try {
      // Example: call your backend to assign a task to multiple matters
      await ClioService.postApiUpdateTaskAssignee({
        matterIds: selectedMatters,
        assigneeType: selectedUserTypes as CustomFields,
        assigneeId: selectedUser
      });

      MySwal.fire({
        icon: "success",
        title: `${selectedMatters.length} matter(s) linked to task successfully.`,
      });
      setSelectedMatters([]);
      setSelectedUserTypes(null);
      setSelectedUser(null);
    } catch (error) {
      MySwal.fire({
        icon: "error",
        title: "Error assigning task to matters.",
      });
    }
  };

  // Pagination handlers
  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <div className="pt-3">
      {/* Controls */}
      <div className="row">
        <div className="col-3">
          <FormControl className="w-100">
            <TextField
              placeholder="Search matters..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 300 }}
            />
          </FormControl>
        </div>
        
        <div className="col-3">
          <FormControl className="w-100">
            <InputLabel id="type-label" required>
              Select User
            </InputLabel>
            <Select
              labelId="type-label"
              className="min-width-200"
              label="Select user*"
              value={selectedUser}
              onChange={(e) => setSelectedUser(Number(e.target.value))}
            >
              {users.map((user) => {
                return (
                  <MenuItem key={user.id} value={user.id!}>
                    {user.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>

        <div className="col-3">
          <FormControl className="w-100">
            <InputLabel id="type-label" required>
              Select Staff Type
            </InputLabel>
            <Select
              labelId="type-label"
              className="min-width-200"
              label="Select type*"
              value={selectedUserTypes}
              onChange={(e) => setSelectedUserTypes(e.target.value)}
            >
              {usersTypesList
                .map((type) => {
                  return (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  );
                })}
            </Select>
          </FormControl>
        </div>
        <div className="col-3">
          <Button
            variant="contained"
            color="primary"
            disabled={!selectedUserTypes || selectedMatters.length === 0 || !selectedUser}
            onClick={handleAssignTask}
          >
            Set
          </Button>
        </div>
      </div>

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={
                    selectedMatters.length > 0 &&
                    selectedMatters.length <
                      filteredMatters.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      ).length
                  }
                  checked={
                    filteredMatters
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .every((m) => selectedMatters.includes(m.id!))
                  }
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </TableCell>
              <TableCell><b>ID</b></TableCell>
              <TableCell><b>Description</b></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredMatters
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((matter) => (
                <TableRow key={matter.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedMatters.includes(matter.id!)}
                      onChange={() => toggleMatter(matter.id!)}
                    />
                  </TableCell>
                  <TableCell>{matter.id}</TableCell>
                  <TableCell
                    style={{
                      maxWidth: 250,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {matter.description}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={filteredMatters.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>
    </div>
  );
}
